import React, { useEffect, useMemo, useState, useRef } from "react";
import "./leasing-form.css";

/* ===== Pomocnicze formatowanie ===== */
const pad2 = (n) => String(n).padStart(2, "0");
const SLOT_MINUTES = 30;

function composeDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}
function addMinutes(date, mins) {
  const d = new Date(date.getTime());
  d.setMinutes(d.getMinutes() + mins);
  return d;
}
function formatHuman(dt) {
  if (!dt) return "";
  const y = dt.getFullYear();
  const m = pad2(dt.getMonth() + 1);
  const d = pad2(dt.getDate());
  const hh = pad2(dt.getHours());
  const mm = pad2(dt.getMinutes());
  return `${d}.${m}.${y} ${hh}:${mm}`;
}

/* ===== Komponent ===== */
export default function Podsumowanie() {
  const [data, setData] = useState(null);

  // page_view do GA4 (opcjonalnie)
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (window.gtag) {
      window.gtag("event", "page_view", { page_path: "/bezplatna-konsultacja/podsumowanie" });
    }
  }, []);

  // Pobierz dane zapisane po submitcie
  useEffect(() => {
    const raw =
      localStorage.getItem("consultation_form") ||
      localStorage.getItem("formData_last_step") ||
      localStorage.getItem("formData");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setData(parsed);
    } catch {
      // nieparsowalne – pomiń
    }
  }, []);

  // Ustal termin (preferuj pola *Human/*ISO; w razie czego przelicz z date+time)
  const term = useMemo(() => {
    if (!data) return null;

    let startISO = data.meetingStartISO || "";
    let endISO = data.meetingEndISO || "";
    let startHuman = data.meetingStartHuman || "";
    let endHuman = data.meetingEndHuman || "";

    if ((!startISO || !endISO) && data.meetingDate && data.meetingTime) {
      const start = composeDateTime(data.meetingDate, data.meetingTime);
      if (start) {
        const end = addMinutes(start, SLOT_MINUTES);
        startISO = start.toISOString();
        endISO = end.toISOString();
        startHuman = formatHuman(start);
        endHuman = formatHuman(end);
      }
    }

    if (!startISO || !endISO) return null;
    return { startISO, endISO, startHuman, endHuman, duration: SLOT_MINUTES };
  }, [data]);

  if (!data) {
    return (
      <div className="leasing-form">
        <div className="summary-container" style={{ maxWidth: 960, margin: "0 auto" }}>
          <img
            src="https://aureus.pl/wp-content/uploads/2021/01/aureus_logo-new.svg"
            alt="Aureus"
            style={{ maxWidth: 180, margin: "0 auto 20px", display: "block" }}
          />
          <h2 style={{ textAlign: "center" }}>Brak danych do wyświetlenia</h2>
          <p style={{ textAlign: "center", color: "#666" }}>
            Otworzyłeś stronę bezpośrednio. Wróć do formularza i wyślij zgłoszenie ponownie.
          </p>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <a href="/bezplatna-konsultacja" className="button-nav-green">Wróć na stronę formularza</a>
          </div>
        </div>
      </div>
    );
  }

  const firstName = data.firstName || "";
  const lastName = data.lastName || "";
  const phone = data.phone || "";
  const email = data.email || "";

  return (
    <div className="leasing-form">
      <div className="summary-container" style={{ maxWidth: 960, margin: "0 auto" }}>
        <img
          src="https://aureus.pl/wp-content/uploads/2021/01/aureus_logo-new.svg"
          alt="Aureus"
          style={{ maxWidth: 180, margin: "0 auto 20px", display: "block" }}
        />

        <h2 style={{ textAlign: "center", marginBottom: 8 }}>Dziękujemy za zgłoszenie!</h2>

        {/* Potwierdzenie + podsumowanie tu, na jednym ekranie */}
        <div className="success-message" style={{ marginBottom: 12 }}>
          <strong>Gotowe!</strong> Zgłoszenie zostało wysłane.
        </div>

        {/* Karta: Termin konsultacji */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>🗓️ Termin konsultacji</h3>
          {term ? (
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {term.startHuman} – {term.endHuman} ({term.duration} min)
              </div>
              <div style={{ color: "#666", marginTop: 4 }}>
                Jeśli potrzebujesz zmienić termin, zadzwoń pod numer: +48 887 445 530
              </div>
            </div>
          ) : (
            <div style={{ color: "#666" }}>
              Nie wybrano konkretnego terminu. Skontaktujemy się w najbliższym możliwym czasie (pon.–pt., 8:00–16:00).
            </div>
          )}
        </div>

        {/* Karta: Dane przekazane w formularzu */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>👤 Dane kontaktowe</h3>
          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", rowGap: 10 }}>
            <div style={{ color: "#666" }}>Imię</div>
            <div style={{ fontWeight: 600 }}>{firstName || "—"}</div>

            <div style={{ color: "#666" }}>Nazwisko</div>
            <div style={{ fontWeight: 600 }}>{lastName || "—"}</div>

            <div style={{ color: "#666" }}>Telefon</div>
            <div style={{ fontWeight: 600 }}>
              {phone ? <a href={`tel:${String(phone).replace(/\s+/g, "")}`}>{phone}</a> : "—"}
            </div>

            <div style={{ color: "#666" }}>Email</div>
            <div style={{ fontWeight: 600 }}>
              {email ? <a href={`mailto:${email}`}>{email}</a> : "— (nie podano)"}
            </div>
          </div>
        </div>

        {/* CTA / powrót */}
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <a
            href="/bezplatna-konsultacja"
            className="button-nav-green"
            style={{ display: "inline-block", textDecoration: "none", padding: "10px 16px", borderRadius: 10, border: "1px solid #1a7f5a" }}
          >
            Wróć na stronę formularza
          </a>
        </div>

        <footer className="footer">
          <div style={{ marginTop: 32, fontSize: 12, textAlign: "center", color: "#555" }}>
            Aureus Leasing Sp. z o.o., ul. Jasnogórska 3, lok. B4, 44-100 Gliwice, NIP 631 264 4058, REGON 242 799 493.
          </div>
        </footer>
      </div>
    </div>
  );
}
